import { chatBotPatch, type ChatConversation } from '@/lib/chatCore'

export const HANDOFF_MUTE_MINUTES = [10, 30, 60] as const

export type HandoffMuteMinutes = (typeof HANDOFF_MUTE_MINUTES)[number]

export type HandoffAlert = {
  key: string
  conversationId: string
  summary: string | null
  at: string | null
  conversation?: ChatConversation
}

const SEEN_LIMIT = 50

export function handoffAlertFrom(payload: unknown): HandoffAlert | null {
  const source = (payload ?? {}) as Record<string, unknown>
  const conversationId = typeof source.conversationId === 'string' && source.conversationId ? source.conversationId : null
  const state = chatBotPatch(payload)
  if (!conversationId || !state.botPaused || state.botPausedReason !== 'handoff') return null
  return {
    key: `${conversationId}:${state.botPausedAt ?? ''}`,
    conversationId,
    summary: state.botHandoffSummary ?? null,
    at: state.botPausedAt ?? null
  }
}

export const handoffMuteKey = (userId: string, companyId: string) => `@Habitar:handoff-alert-muted:${userId}:${companyId}`

export const handoffSeenKey = (userId: string, companyId: string) => `@Habitar:handoff-alert-seen:${userId}:${companyId}`

export function mutedUntil(raw: string | null, now = Date.now()): number | null {
  const until = Number(raw)
  return raw && Number.isFinite(until) && until > now ? until : null
}

export const muteUntil = (minutes: HandoffMuteMinutes, now = Date.now()) => now + minutes * 60_000

export function parseSeen(raw: string | null): string[] {
  try {
    const parsed = JSON.parse(raw ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

export const addSeen = (seen: string[], keys: string[]): string[] => [...new Set([...seen, ...keys])].slice(-SEEN_LIMIT)

export type HandoffAlertContext = {
  userId: string
  viewingConversationId: string | null
  mutedUntil: number | null
  seen: string[]
  now?: number
}

export function shouldAlertHandoff(
  alert: HandoffAlert,
  { userId, viewingConversationId, mutedUntil, seen, now = Date.now() }: HandoffAlertContext
): boolean {
  if (mutedUntil !== null && mutedUntil > now) return false
  if (seen.includes(alert.key)) return false
  if (viewingConversationId === alert.conversationId) return false
  if (alert.conversation?.closed) return false
  if (alert.conversation?.assignedUserId && alert.conversation.assignedUserId !== userId) return false
  return true
}

export const handoffAlertTitle = (count: number) =>
  count <= 1 ? 'A IA está pedindo ajuda de um corretor' : `A IA está pedindo ajuda em ${count} conversas`

export const handoffBlinkTitle = (count: number) => `(${count}) Cliente aguardando corretor`
